import React, { useState, useEffect, useCallback, CSSProperties } from 'react'
import { uniqWith, isEqual } from 'lodash-es'

type ImageProps = {
  src: string
  width: number | null
  height: number | null
}

type MultiImageProps = {
  imgSrc: string
  imgWidth?: number | null
  imgHeight?: number | null
  imgAlt?: string
  containerWidth: number
  containerHeight: number
  onClick?: () => void
  className?: string
}

const MultiImage: React.FC<MultiImageProps> = ({
  imgSrc = '',
  imgWidth = null,
  imgHeight = null,
  containerWidth = 0,
  containerHeight = 0,
  onClick = () => {},
  className = '',
}) => {
  const [images, setImages] = useState<ImageProps[]>([{ src: imgSrc, width: imgWidth, height: imgHeight }])
  const [prevImageSrc, setPrevImageSrc] = useState(imgSrc)

  // Update state when props change
  useEffect(() => {
    if (prevImageSrc !== imgSrc) {
      const nextImage = { src: imgSrc, width: imgWidth, height: imgHeight }
      const nextImages = uniqWith([...images, nextImage], isEqual)
      setImages(nextImages)
      setPrevImageSrc(imgSrc)
    }
  }, [imgSrc, imgWidth, imgHeight, prevImageSrc, images])

  // Generate image content
  const getImagesContent = useCallback(() => {
    return images.map(imageProps => {
      const { src } = imageProps
      const imageStyle: React.CSSProperties = {
        width: containerWidth,
        height: containerHeight,
        backgroundImage: `url('${src}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        position: 'absolute',
        top: 0,
        left: 0,
      }
      return <div key={`image-${src}`} style={imageStyle}></div>
    })
  }, [images, containerWidth, containerHeight])

  // Component styles
  const style: CSSProperties = {
    width: containerWidth,
    height: containerHeight,
  }

  return (
    <div onClick={onClick} className={className} style={style}>
      {getImagesContent()}
    </div>
  )
}

export default MultiImage
